"use client";

import type { Editor } from "@tiptap/core";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface ScribeSuggestionsActions {
  setFocusedQuestionId: (id: string | null) => void;
  registerEditor: (questionId: string, editor: Editor) => () => void;
  insertIntoFocused: (content: string) => boolean;
}

interface ScribeSuggestionsState {
  focusedQuestionId: string | null;
}

// Two contexts so consumers that only need the stable actions (e.g.
// NoteAnswerEditor registering its TipTap editor) don't re-run their effects
// on every focus change. Only the sidebar reads the changing focus state.
const ActionsContext = createContext<ScribeSuggestionsActions | null>(null);
const StateContext = createContext<ScribeSuggestionsState | null>(null);

/**
 * Tracks the currently focused note question's TipTap editor so the
 * suggestions sidebar can drive insertions into the right editor instance.
 *
 * Focus updates on every TipTap `focus` event; we deliberately do NOT clear on
 * blur, because clicking a sidebar button blurs the editor on the way to the
 * insert. Holding the last-focused id means the click still has somewhere to
 * go.
 */
export function ScribeSuggestionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [focusedQuestionId, setFocusedQuestionIdState] = useState<
    string | null
  >(null);
  const focusedQuestionIdRef = useRef<string | null>(null);
  const editorsRef = useRef(new Map<string, Editor>());

  const setFocusedQuestionId = useCallback((id: string | null) => {
    focusedQuestionIdRef.current = id;
    setFocusedQuestionIdState(id);
  }, []);

  const registerEditor = useCallback((questionId: string, editor: Editor) => {
    editorsRef.current.set(questionId, editor);
    return () => {
      // Only drop from the registry; don't touch focus state. Unregister can
      // fire from effect cleanup that runs on re-renders (not just unmount),
      // and clearing focus there causes the sidebar to flicker empty
      // mid-render. insertIntoFocused already no-ops if the focused editor
      // isn't registered, so this stays correct.
      const current = editorsRef.current.get(questionId);
      if (current === editor) {
        editorsRef.current.delete(questionId);
      }
    };
  }, []);

  const insertIntoFocused = useCallback((content: string) => {
    const id = focusedQuestionIdRef.current;
    if (!id) return false;
    const editor = editorsRef.current.get(id);
    if (!editor || editor.isDestroyed) return false;

    // Insert as paragraph nodes — the text + hardBreak shape that the
    // @mention command uses doesn't render visible line breaks when the
    // sidebar fires the insertion (the @mention path pairs it with
    // deleteRange, which behaves differently). Paragraph nodes render
    // line breaks reliably here and that's what providers expect.
    const trimmed = content.trim();
    if (!trimmed) return false;
    const paragraphs = trimmed.split(/\r?\n/).map((line) => ({
      type: "paragraph",
      content: line ? [{ type: "text", text: line }] : [],
    }));

    editor.chain().focus().insertContent(paragraphs).run();
    return true;
  }, []);

  // Stable across renders — only the focus state context re-renders consumers
  // when focusedQuestionId changes.
  const actions = useMemo<ScribeSuggestionsActions>(
    () => ({ setFocusedQuestionId, registerEditor, insertIntoFocused }),
    [setFocusedQuestionId, registerEditor, insertIntoFocused],
  );

  const state = useMemo<ScribeSuggestionsState>(
    () => ({ focusedQuestionId }),
    [focusedQuestionId],
  );

  return (
    <ActionsContext.Provider value={actions}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </ActionsContext.Provider>
  );
}

export function useScribeSuggestionsActions(): ScribeSuggestionsActions {
  const ctx = useContext(ActionsContext);
  if (!ctx) {
    throw new Error(
      "useScribeSuggestionsActions must be used inside ScribeSuggestionsProvider",
    );
  }
  return ctx;
}

export function useScribeSuggestionsFocus(): ScribeSuggestionsState {
  const ctx = useContext(StateContext);
  if (!ctx) {
    throw new Error(
      "useScribeSuggestionsFocus must be used inside ScribeSuggestionsProvider",
    );
  }
  return ctx;
}

/**
 * Combined accessor for callers that need both. Re-renders on focus changes.
 */
export function useScribeSuggestions(): ScribeSuggestionsActions &
  ScribeSuggestionsState {
  return { ...useScribeSuggestionsActions(), ...useScribeSuggestionsFocus() };
}

/**
 * Optional read of the actions context — returns null when no provider is
 * mounted. Used by editors that are also rendered outside the scribe
 * playground (e.g. other note views) so they don't crash on missing context.
 */
export function useOptionalScribeSuggestionsActions(): ScribeSuggestionsActions | null {
  return useContext(ActionsContext);
}
